// Caixa de entrada do aluno: lista mensagens (broadcast prof→turma + diretas
// se vierem). Marca lida/não-lida. Polling junto com notificações.

import { computed, signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import {
  AlertCircle,
  Check,
  CheckSquare,
  Eye,
  Loader2,
  Mail,
  MailOpen,
  MailQuestion,
  RefreshCw,
  Square,
  Trash2,
  X
} from "lucide-react";
import { api, ApiError, type InboxMessageRow } from "./api";
import { extractPayLink, linkify } from "./linkify";
import { openAlert } from "./ConfirmPromptModal";

interface InboxState {
  phase: "idle" | "loading" | "ready" | "error";
  list: InboxMessageRow[];
  unreadCount: number;
  errMsg: string | null;
  lastLoadedAt: number;
}

export const inboxState = signal<InboxState>({
  phase: "idle",
  list: [],
  unreadCount: 0,
  errMsg: null,
  lastLoadedAt: 0
});

/** Pra mostrar a badge na sidebar (item Mensagens). */
export const inboxUnreadCount = computed(() => inboxState.value.unreadCount);

export async function loadInbox(opts: { silent?: boolean } = {}) {
  if (!opts.silent) {
    inboxState.value = { ...inboxState.value, phase: "loading", errMsg: null };
  }
  try {
    const r = await api.listInbox();
    inboxState.value = {
      ...inboxState.value,
      phase: "ready",
      list: r.messages,
      unreadCount: r.unreadCount,
      lastLoadedAt: Date.now()
    };
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    inboxState.value = {
      ...inboxState.value,
      phase: "error",
      errMsg: e instanceof Error ? e.message : String(e)
    };
  }
}

let _pollHandle: number | null = null;
export function startInboxPolling() {
  if (_pollHandle != null) return;
  void loadInbox({ silent: true });
  _pollHandle = window.setInterval(() => {
    void loadInbox({ silent: true });
  }, 60_000);
}

async function bulkSetRead(ids: string[], read: boolean) {
  inboxState.value = {
    ...inboxState.value,
    list: inboxState.value.list.map((m) =>
      ids.includes(m.id)
        ? { ...m, readAt: read ? new Date().toISOString() : null }
        : m
    )
  };
  const newUnread = inboxState.value.list.filter((m) => !m.readAt).length;
  inboxState.value = { ...inboxState.value, unreadCount: newUnread };
  try {
    await Promise.all(
      ids.map((id) =>
        (read ? api.markMessageRead(id) : api.markMessageUnread(id)).catch(
          () => null
        )
      )
    );
  } catch (e) {
    void loadInbox({ silent: true });
  }
}

export async function setMessageRead(id: string, read: boolean) {
  // Otimista
  inboxState.value = {
    ...inboxState.value,
    list: inboxState.value.list.map((m) =>
      m.id === id
        ? { ...m, readAt: read ? new Date().toISOString() : null }
        : m
    ),
    unreadCount: Math.max(
      0,
      inboxState.value.unreadCount + (read ? -1 : +1)
    )
  };
  try {
    if (read) await api.markMessageRead(id);
    else await api.markMessageUnread(id);
  } catch (e) {
    void openAlert({ title: "Erro", message: e instanceof Error ? e.message : String(e) });
    void loadInbox({ silent: true });
  }
}

export function MensagensView({ compact = false }: { compact?: boolean } = {}) {
  useEffect(() => {
    void loadInbox();
  }, []);

  const state = inboxState.value;
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [openItem, setOpenItem] = useState<InboxMessageRow | null>(null);
  const selCount = selected.size;
  const inSelectionMode = selCount > 0;

  function toggle(id: string) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  }
  function selectAll() {
    setSelected(new Set(state.list.map((m) => m.id)));
  }
  function clearSelection() {
    setSelected(new Set());
  }
  function openModal(m: InboxMessageRow) {
    setOpenItem(m);
    if (!m.readAt) void setMessageRead(m.id, true);
  }
  async function bulkAction(kind: "read" | "unread") {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    await bulkSetRead(ids, kind === "read");
    clearSelection();
  }

  return (
    <div class="space-y-5">
      {inSelectionMode ? (
        <div class="sticky top-0 z-10 -mx-5 flex flex-wrap items-center justify-between gap-2 border-y border-blue-500/30 bg-blue-500/10 px-5 py-3 backdrop-blur-md md:-mx-8 md:px-8">
          <div class="flex items-center gap-3">
            <button
              type="button"
              onClick={clearSelection}
              title="Limpar seleção"
              aria-label="Limpar seleção"
              class="grid size-8 place-items-center rounded-md border border-border bg-background hover:bg-accent"
            >
              <X className="size-4" />
            </button>
            <span class="text-sm font-bold">
              {selCount} selecionada{selCount === 1 ? "" : "s"}
            </span>
            <button
              type="button"
              onClick={selectAll}
              class="text-xs font-semibold text-blue-700 hover:underline dark:text-blue-400"
            >
              Selecionar tudo ({state.list.length})
            </button>
          </div>
          <div class="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => bulkAction("read")}
              class="inline-flex items-center gap-1.5 rounded-md border border-blue-500/40 bg-blue-500/15 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-500/25 dark:text-blue-400"
            >
              <Check className="size-3.5" /> Marcar lidas
            </button>
            <button
              type="button"
              onClick={() => bulkAction("unread")}
              class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent"
            >
              <Eye className="size-3.5" /> Não-lidas
            </button>
          </div>
        </div>
      ) : compact ? null : (
        <header class="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 class="flex items-center gap-2 text-2xl font-bold tracking-tight">
              <Mail className="size-6 text-amber-600 dark:text-amber-400" />
              Mensagens
            </h1>
            <p class="mt-1 text-sm text-muted-foreground">
              Clique para abrir; ou selecione várias pra agir em lote.
            </p>
          </div>
          <div class="flex flex-wrap items-center gap-2">
            {state.list.length > 0 && (
              <button
                type="button"
                onClick={selectAll}
                class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent"
              >
                <CheckSquare className="size-3.5" />
                Selecionar tudo
              </button>
            )}
            <button
              type="button"
              onClick={() => void loadInbox()}
              title="Atualizar"
              aria-label="Atualizar"
              class="grid size-8 place-items-center rounded-md border border-border bg-background text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <RefreshCw className="size-3.5" />
            </button>
          </div>
        </header>
      )}

      {state.phase === "loading" && state.list.length === 0 && (
        <p class="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Carregando…
        </p>
      )}

      {state.phase === "error" && (
        <div class="rounded-md border border-destructive/30 bg-destructive/5 p-4">
          <p class="flex items-center gap-2 text-sm text-destructive">
            <AlertCircle className="size-4" />
            {state.errMsg}
          </p>
        </div>
      )}

      {state.phase === "ready" && state.list.length === 0 && (
        <div class="rounded-md border border-dashed border-border bg-card/40 p-10 text-center">
          <MailQuestion className="mx-auto mb-3 size-8 text-muted-foreground/60" />
          <p class="text-sm text-muted-foreground">
            Nenhuma mensagem por enquanto.
          </p>
        </div>
      )}

      <ul class="space-y-2">
        {state.list.map((m) => (
          <MessageItem
            key={m.id}
            m={m}
            selected={selected.has(m.id)}
            onToggle={() => toggle(m.id)}
            onOpen={() => openModal(m)}
            onRead={() => setMessageRead(m.id, true)}
            onUnread={() => setMessageRead(m.id, false)}
          />
        ))}
      </ul>

      {openItem && (
        <MessageModal
          m={openItem}
          onClose={() => setOpenItem(null)}
          onUnread={() => {
            void setMessageRead(openItem.id, false);
            setOpenItem(null);
          }}
        />
      )}
    </div>
  );
}

function MessageItem({
  m,
  selected,
  onToggle,
  onOpen,
  onRead,
  onUnread
}: {
  m: InboxMessageRow;
  selected: boolean;
  onToggle: () => void;
  onOpen: () => void;
  onRead: () => void;
  onUnread: () => void;
}) {
  const isUnread = !m.readAt;
  return (
    <li
      class={`group rounded-md border px-4 py-3 transition-colors cursor-pointer ${
        selected
          ? "border-blue-500 bg-blue-500/10 ring-1 ring-blue-500/30"
          : isUnread
          ? "border-amber-500/40 bg-amber-500/5 hover:bg-amber-500/10"
          : "border-border bg-card/60 hover:bg-accent/30"
      }`}
      onClick={onOpen}
    >
      <div class="flex flex-wrap items-start gap-3">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          title={selected ? "Desmarcar" : "Selecionar"}
          aria-label={selected ? "Desmarcar" : "Selecionar"}
          class={`mt-0.5 grid size-5 shrink-0 place-items-center rounded border-2 transition-colors ${
            selected
              ? "border-blue-500 bg-blue-500 text-white"
              : "border-border bg-background text-transparent hover:border-blue-500"
          }`}
        >
          <Check className="size-3.5" strokeWidth={3} />
        </button>
        <div
          class="grid size-9 shrink-0 place-items-center rounded-md bg-amber-500/10 text-amber-600 dark:text-amber-400"
          aria-hidden="true"
        >
          <MailOpen className="size-4" />
        </div>
        <div class="min-w-0 flex-1">
          <div class="flex flex-wrap items-center gap-2">
            <span class="text-sm font-bold">{m.title}</span>
            {isUnread && (
              <span class="rounded bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
                NOVA
              </span>
            )}
            <span class="text-[11px] text-muted-foreground">
              de {m.fromName} · {formatRelative(m.createdAt)}
            </span>
          </div>
          <p class="mt-1 line-clamp-2 text-xs leading-relaxed text-muted-foreground">
            {m.body}
          </p>
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (isUnread) onRead();
            else onUnread();
          }}
          title={isUnread ? "Marcar como lida" : "Marcar como não-lida"}
          aria-label={isUnread ? "Marcar como lida" : "Marcar como não-lida"}
          class="grid size-7 shrink-0 place-items-center rounded-md text-muted-foreground opacity-0 transition-all group-hover:opacity-100 hover:bg-accent hover:text-foreground"
        >
          <Check className={`size-3.5 ${isUnread ? "" : "opacity-30"}`} />
        </button>
      </div>
    </li>
  );
}

export function MessageModal({
  m,
  onClose,
  onUnread
}: {
  m: InboxMessageRow;
  onClose: () => void;
  onUnread: () => void;
}) {
  return (
    <div
      class="fixed inset-0 z-50 grid place-items-center bg-background/80 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        class="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-md border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header class="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
          <div class="flex items-start gap-3 min-w-0">
            <div class="grid size-10 shrink-0 place-items-center rounded-md bg-primary/10 text-primary">
              <MailOpen className="size-5" />
            </div>
            <div class="min-w-0">
              <h2 class="text-base font-bold">{m.title}</h2>
              <p class="mt-0.5 text-xs text-muted-foreground">
                de <span class="font-semibold">{m.fromName}</span> ·{" "}
                {new Date(m.createdAt).toLocaleString("pt-BR")}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            title="Fechar"
            aria-label="Fechar"
            class="grid size-8 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </header>
        <div class="flex-1 overflow-y-auto px-5 py-4">
          {(() => {
            const payLink = extractPayLink(m.body);
            return (
              <>
                {payLink && (
                  <a
                    href={payLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    class="mb-3 inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-bold text-primary-foreground hover:bg-primary/90"
                  >
                    Abrir link de pagamento →
                  </a>
                )}
                <p class="whitespace-pre-wrap text-sm leading-relaxed">
                  {linkify(m.body)}
                </p>
              </>
            );
          })()}
        </div>
        <footer class="flex flex-wrap items-center justify-end gap-2 border-t border-border bg-muted/20 px-5 py-3">
          <button
            type="button"
            onClick={onUnread}
            class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent"
          >
            <Eye className="size-3.5" /> Marcar como não-lida
          </button>
          <button
            type="button"
            onClick={onClose}
            class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90"
          >
            Fechar
          </button>
        </footer>
      </div>
    </div>
  );
}

function formatRelative(iso: string): string {
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  if (diff < 60_000) return "agora";
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)} min`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3600_000)} h`;
  if (diff < 7 * 86_400_000) return `${Math.floor(diff / 86_400_000)} d`;
  return new Date(iso).toLocaleDateString("pt-BR");
}
